/**
 * `useContractOptions()` — charge la liste des rôles + régions (built-in +
 * custom) depuis `/api/contracts/options/list`. Utilisé par ContractCard
 * pour peupler les 2 dropdowns du formulaire de préparation.
 *
 * Retourne également des mutations `createRole` / `createRegion` /
 * `deleteRole` / `deleteRegion` qui, en cas de succès, rechargent
 * automatiquement la liste. Ainsi une entrée fraîchement créée est
 * immédiatement sélectionnable dans le formulaire.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import { api } from "../lib/api";


export function useContractOptions() {
    const [loading, setLoading] = useState(true);
    const [roles, setRoles] = useState([]);
    const [regions, setRegions] = useState([]);
    const [classifications, setClassifications] = useState([]);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/contracts/options/list");
            setRoles(data.roles || []);
            setRegions(data.regions || []);
            setClassifications(data.classifications || []);
        } catch (e) {
            toast.error("Impossible de charger la liste des rôles et régions");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { reload(); }, [reload]);

    const createRole = useCallback(async ({ label, label_long, template_base }) => {
        const { data } = await api.post("/contracts/options/roles", {
            label, label_long, template_base,
        });
        toast.success(`Rôle « ${label} » ajouté`);
        await reload();
        return data;
    }, [reload]);

    const createRegion = useCallback(async ({ label }) => {
        const { data } = await api.post("/contracts/options/regions", { label });
        toast.success(`Région « ${label} » ajoutée`);
        await reload();
        return data;
    }, [reload]);

    const createClassification = useCallback(async ({ position, coefficient, label }) => {
        const { data } = await api.post("/contracts/options/classifications", {
            position, coefficient, label,
        });
        toast.success(`Classification ETAM ${position} · ${coefficient} ajoutée`);
        await reload();
        return data;
    }, [reload]);

    const deleteRole = useCallback(async (key) => {
        await api.delete(`/contracts/options/roles/${key}`);
        toast.success("Rôle supprimé");
        await reload();
    }, [reload]);

    const deleteRegion = useCallback(async (key) => {
        await api.delete(`/contracts/options/regions/${key}`);
        toast.success("Région supprimée");
        await reload();
    }, [reload]);

    const deleteClassification = useCallback(async (key) => {
        await api.delete(`/contracts/options/classifications/${key}`);
        toast.success("Classification supprimée");
        await reload();
    }, [reload]);

    return {
        loading, roles, regions, classifications,
        createRole, createRegion, createClassification,
        deleteRole, deleteRegion, deleteClassification,
    };
}
